import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, PlayCircle, Sparkles, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo, LangToggle, DemoBadge } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { useApp } from "@/lib/i18n";
import { getDemo } from "@/components/landing/demoData";

export const Navbar = () => {
  const { L } = useApp();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const links = [[L("Fungsi", "Features"), "#fungsi"], [L("Cara Berfungsi", "How it works"), "#cara"], [L("Harga", "Pricing"), "#harga"], ["FAQ", "#faq"]].map(([l, h]) => [l, `/${h}`]);
  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/60 bg-white/85 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Logo />
        <nav className="hidden items-center gap-7 md:flex" aria-label="Utama">
          {links.map(([label, href]) => <a key={href} href={href} className="text-sm font-medium text-slate-600 hover:text-slate-900" data-testid={`nav-${href.slice(2)}`}>{label}</a>)}
          <Link to="/contact" className="text-sm font-medium text-slate-600 hover:text-slate-900" data-testid="nav-contact">{L("Hubungi", "Contact")}</Link>
        </nav>
        <div className="flex items-center gap-2">
          <LangToggle />
          {user ? (
            <Button asChild className="rounded-xl bg-slate-900" data-testid="nav-dashboard-btn"><Link to="/app">Dashboard</Link></Button>
          ) : (
            <>
              <Button asChild variant="ghost" className="hidden rounded-xl sm:inline-flex" data-testid="nav-login-btn"><Link to="/login">{L("Log Masuk", "Log In")}</Link></Button>
              <Button asChild className="rounded-xl bg-violet-600 hover:bg-violet-700" data-testid="nav-try-btn"><Link to="/register">{L("Cuba Sekarang", "Try Now")}</Link></Button>
            </>
          )}
          <button className="md:hidden" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="landing-menu-btn">{open ? <X size={20} /> : <Menu size={20} />}</button>
        </div>
      </div>
      {open && (
        <nav className="flex flex-col gap-1 border-t border-slate-200 bg-white px-4 py-3 md:hidden">
          {links.map(([label, href]) => <a key={href} href={href} onClick={() => setOpen(false)} className="rounded-lg px-2 py-2 text-sm font-medium">{label}</a>)}
          <Link to="/contact" onClick={() => setOpen(false)} className="rounded-lg px-2 py-2 text-sm font-medium" data-testid="mobile-nav-contact">{L("Hubungi", "Contact")}</Link>
          {!user && <Link to="/login" className="rounded-lg px-2 py-2 text-sm font-medium">{L("Log Masuk", "Log In")}</Link>}
        </nav>
      )}
    </header>
  );
};

const PromptPanel = () => {
  const { L, lang } = useApp();
  const { minutes: DEMO_MINUTES, notes: DEMO_NOTES, actions: DEMO_ACTIONS } = getDemo(lang);
  const prompts = [
    [L("Hasilkan minit mesyuarat", "Generate meeting minutes"), DEMO_MINUTES.split("\n").slice(2, 8).join("\n").replace(/[#*>]/g, "")],
    [L("Senaraikan keputusan", "List the decisions"), DEMO_NOTES.content.decisions.map((d) => `✓ ${d.text}  [00:19]`).join("\n") + "\n" + DEMO_ACTIONS.map((a) => `→ ${a.task} — ${a.owner}`).join("\n")],
    [L("Tukar kepada nota kuliah", "Convert into lecture notes"), L("Objektif Pembelajaran\nKonsep Utama: Sistem e-Kehadiran\nIstilah Penting: go-live, lesen server\nSoalan Ulang Kaji (5)\n\n→ Eksport DOCX / PDF / TXT / PPTX / CSV & kongsi ke WhatsApp", "Learning Objectives\nKey Concept: e-Attendance System\nKey Terms: go-live, server licence\nRevision Questions (5)\n\n→ Export DOCX / PDF / TXT / PPTX / CSV & share to WhatsApp")],
  ];
  const [active, setActive] = useState(0);
  return (
    <div className="fade-up rounded-2xl border border-slate-200 bg-white p-5 shadow-xl shadow-slate-900/5" style={{ animationDelay: ".3s" }} data-testid="prompt-panel">
      <div className="flex items-center justify-between"><span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-violet-700"><Sparkles size={14} /> {L("Contoh arahan", "Example prompts")}</span><DemoBadge /></div>
      <div className="mt-4 flex flex-wrap gap-2">
        {prompts.map(([p], i) => (
          <button key={p} onClick={() => setActive(i)} aria-pressed={active === i} data-testid={`prompt-chip-${i}`}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${active === i ? "border-violet-600 bg-violet-600 text-white" : "border-slate-200 text-slate-700 hover:border-violet-400"}`}>{p}</button>
        ))}
      </div>
      <pre className="mt-4 min-h-[150px] whitespace-pre-wrap rounded-xl bg-slate-50 p-4 font-sans text-sm leading-relaxed text-slate-700" data-testid="prompt-output">{prompts[active][1]}</pre>
    </div>
  );
};

export const Hero = () => {
  const { L } = useApp();
  return (
    <section className="relative overflow-hidden">
      <div className="dot-bg absolute inset-0 opacity-60 [mask-image:radial-gradient(ellipse_at_top,black,transparent_70%)]" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 md:py-24 lg:grid-cols-12 lg:px-8">
        <div className="lg:col-span-6">
          <span className="fade-up inline-flex items-center gap-2 rounded-full bg-violet-100 px-3 py-1 text-xs font-semibold text-violet-800">{L("Untuk organisasi, SME & pendidikan di Malaysia", "For organisations, SMEs & education in Malaysia")}</span>
          <h1 className="fade-up mt-6 text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl" style={{ animationDelay: ".08s" }}>
            {L("Fokus pada mesyuarat.", "Focus on the meeting.")} <span className="text-violet-600">{L("Biar AI uruskan nota.", "Let AI handle the notes.")}</span>
          </h1>
          <p className="fade-up mt-6 max-w-xl text-base text-slate-600 md:text-lg" style={{ animationDelay: ".16s" }}>
            {L("Tukar rakaman kepada transkrip, minit, keputusan dan tindakan susulan dalam satu tempat.", "Turn recordings into transcripts, minutes, decisions and follow-up actions in one place.")}
          </p>
          <div className="fade-up mt-9 flex flex-wrap gap-3" style={{ animationDelay: ".24s" }}>
            <Button asChild size="lg" className="h-12 rounded-xl bg-violet-600 px-6 hover:bg-violet-700" data-testid="hero-start-btn"><Link to="/register">{L("Mulakan Sekarang", "Get Started")} <ArrowRight size={18} className="ml-1" /></Link></Button>
            <Button asChild size="lg" variant="outline" className="h-12 rounded-xl px-6" data-testid="hero-demo-btn"><a href="#demo"><PlayCircle size={18} className="mr-1" /> {L("Lihat Demo", "View Demo")}</a></Button>
          </div>
          <p className="fade-up mt-6 text-xs text-slate-500" style={{ animationDelay: ".3s" }}>{L("BM · English · campuran BM/EN", "Malay · English · mixed Malay/English")}</p>
        </div>
        <div className="lg:col-span-6"><PromptPanel /></div>
      </div>
    </section>
  );
};
